import {
  Divider,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  Tooltip,
} from '@chakra-ui/react';
import {
  faCheck,
  faChevronDown,
  faCircleDot,
  faDiamond,
  faDrawPolygon,
  faMagicWandSparkles,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PolylineIcon } from '../../assets/icons/draw-polyline.svg';
import { ReactComponent as RectangleIcon } from '../../assets/icons/draw-rectangle.svg';
import { ReactComponent as ExtrudeIcon } from '../../assets/icons/extrude-icon.svg';
import { useSheetShapes } from '../../hooks/sheet-shapes';
import { useSheetViewer } from '../common/SheetViewer';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useUserTenant } from '../../services/auth-info';
import { ShapeDrawingMode } from './pdf-shapes/SheetShapeDrawing';
import { Substrate } from './ProductChoices';
import { useOrderLabelsProtan } from 'src/hooks/protan-data';
// eslint-disable-next-line complexity
export default function SheetShapesToolbar() {
  const enableMagicSheetShapes = useFlag('enable-magic-sheet-shapes');
  const enableDrainageLine = useFlag('enable-drainage-line');

  const {
    drawing: {
      deactivate: deactivateDrawing,
      shapeToEdit,
      hasResult: hasDrawingResult,
      submit: submitDrawing,
      isSubmitting: isSubmittingDrawing,
      isDrawing,
      mode: drawingMode,
      activate: activateDrawing,
      minHeight,
      setMinHeight,
    },
  } = useSheetShapes();

  const { isProtan } = useUserTenant();
  const backgroundColor = 'gray.800';
  const iconColor = 'gray.50';
  const borderColor = 'gray.700';
  const activeBackgroundColor = 'teal.700';
  const activeIconColor = 'teal.200';

  const { t } = useTranslation('project');

  const { page, calibration } = useSheetViewer();

  const { substrate } = useOrderLabelsProtan();

  useEffect(() => {
    if (!calibration.calibration) {
      deactivateDrawing();
    }
  }, [calibration.calibration, deactivateDrawing]);

  useHotkeys('esc', deactivateDrawing, { enabled: isDrawing });
  useHotkeys('enter', () => submitDrawing(), [submitDrawing], {
    enabled: isDrawing && hasDrawingResult && !isSubmittingDrawing,
  });
  useHotkeys('shift+s', () => activateDrawing(), [activateDrawing], {
    enabled: !!page && !!calibration.calibration,
  });

  useHotkeys(
    'space',
    async () => {
      await submitDrawing();
      activateDrawing();
    },
    {
      enabled:
        isDrawing &&
        hasDrawingResult &&
        !isSubmittingDrawing &&
        !!calibration.calibration,
    },
    [submitDrawing, activateDrawing]
  );

  if (!isDrawing) {
    return null;
  }
  return (
    <HStack
      backgroundColor={backgroundColor}
      rounded={50}
      px={1}
      height={8}
      borderColor={borderColor}
      borderWidth="1px"
      mb={1}
    >
      <Tooltip label={t('shapes.toolbar.point')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw point"
          size="xs"
          icon={
            <SparkelIcon
              icon={faCircleDot}
              color={
                drawingMode === ShapeDrawingMode.Point
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Point}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Point);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.polyline')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw polyline"
          size="xs"
          icon={
            <SparkelIcon
              as={PolylineIcon}
              color={
                drawingMode === ShapeDrawingMode.LineString
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.LineString}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.LineString);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.rectangle')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw rectangle"
          size="xs"
          icon={
            <SparkelIcon
              as={RectangleIcon}
              color={
                drawingMode === ShapeDrawingMode.Rectangle
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Rectangle}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Rectangle);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.polygon')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw polygon"
          size="xs"
          icon={
            <SparkelIcon
              icon={faDrawPolygon}
              color={
                drawingMode === ShapeDrawingMode.Polygon
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Polygon}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Polygon);
          }}
        />
      </Tooltip>
      {isProtan ? (
        <>
          <Tooltip label={t('shapes.toolbar.wedge')}>
            <IconButton
              variant={'ghost'}
              colorScheme={'teal'}
              _active={{ backgroundColor: activeBackgroundColor }}
              _hover={{ backgroundColor: 'blackAlpha.100' }}
              aria-label="draw wedge"
              size="xs"
              icon={
                <SparkelIcon
                  icon={faDiamond}
                  color={
                    drawingMode === ShapeDrawingMode.Wedges
                      ? activeIconColor
                      : iconColor
                  }
                />
              }
              isActive={drawingMode === ShapeDrawingMode.Wedges}
              isDisabled={!!shapeToEdit}
              onClick={() => {
                activateDrawing(ShapeDrawingMode.Wedges);
              }}
            />
          </Tooltip>
          {enableDrainageLine && (
            <Tooltip label="Drainage line">
              <IconButton
                variant={'ghost'}
                colorScheme={'teal'}
                _active={{ backgroundColor: activeBackgroundColor }}
                _hover={{ backgroundColor: 'blackAlpha.100' }}
                aria-label="draw drainage line"
                size="xs"
                icon={
                  <SparkelIcon
                    icon={faChevronDown}
                    color={
                      drawingMode === ShapeDrawingMode.DrainLine
                        ? activeIconColor
                        : iconColor
                    }
                  />
                }
                isActive={drawingMode === ShapeDrawingMode.DrainLine}
                isDisabled={!!shapeToEdit}
                onClick={() => {
                  activateDrawing(ShapeDrawingMode.DrainLine);
                }}
              />
            </Tooltip>
          )}
          {enableDrainageLine && drawingMode === ShapeDrawingMode.DrainLine && (
            <>
              <Divider orientation="vertical" height={'70%'} />
              <InputGroup size={'xs'}>
                <InputLeftElement>
                  <SparkelIcon as={ExtrudeIcon} color={activeIconColor} />
                </InputLeftElement>
                <NumberInput
                  variant={'filled'}
                  max={1000}
                  min={80} // Stop from setting zero-thickness
                  color={iconColor}
                  precision={0}
                  step={15}
                  defaultValue={minHeight ? minHeight * 1000 : 1000}
                  onChange={(value) => {
                    const mm = parseFloat(value);
                    // First subtract base, then round up to nearest 60mm, then add base back
                    const base = substrate === Substrate.Concrete ? 50 : 80;
                    const roundedMm =
                      base + Math.ceil(Math.max(0, mm - base) / 15) * 15;
                    setMinHeight(roundedMm / 1000); // convert to meters
                  }}
                >
                  <NumberInputField
                    paddingLeft={6}
                    paddingRight={0}
                    borderRadius={'md'}
                    autoFocus
                    width={24}
                    backgroundColor={borderColor}
                    _hover={{ backgroundColor: 'blackAlpha.500' }}
                  />
                </NumberInput>
              </InputGroup>
            </>
          )}
        </>
      ) : null}
      {enableMagicSheetShapes ? (
        <Tooltip label={t('shapes.toolbar.magic-polygon')}>
          <IconButton
            variant={'ghost'}
            colorScheme={'teal'}
            _active={{ backgroundColor: activeBackgroundColor }}
            _hover={{ backgroundColor: 'blackAlpha.100' }}
            aria-label="draw magic polygon"
            size="xs"
            icon={
              <SparkelIcon
                icon={faMagicWandSparkles}
                color={
                  drawingMode === ShapeDrawingMode.MagicPolygon
                    ? activeIconColor
                    : iconColor
                }
              />
            }
            isActive={drawingMode === ShapeDrawingMode.MagicPolygon}
            isDisabled={!!shapeToEdit}
            onClick={() => {
              activateDrawing(ShapeDrawingMode.MagicPolygon);
            }}
          />
        </Tooltip>
      ) : null}
      <Divider orientation="vertical" height={'70%'} />
      <Tooltip label={t('shapes.cancel-drawing')}>
        <IconButton
          aria-label="Cancel drawing"
          colorScheme="gray"
          variant={'ghost'}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          size="xs"
          onClick={deactivateDrawing}
          isRound
          icon={<SparkelIcon icon={faTimes} color={iconColor} />}
        ></IconButton>
      </Tooltip>
      <Tooltip
        label={
          hasDrawingResult
            ? t('shapes.save-tooltip')
            : t('shapes.save-disabled-tooltip')
        }
      >
        <IconButton
          aria-label="Save"
          size="xs"
          onClick={() => submitDrawing()}
          isLoading={isSubmittingDrawing}
          isRound
          isDisabled={!hasDrawingResult}
          icon={<SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />}
        ></IconButton>
      </Tooltip>
    </HStack>
  );
}
